import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Line-at-a-time Markdown subset for AI analysis output. Kept separate from
 * common/MarkdownRenderer: that one also parses italics and fenced code
 * and merges runs of list items into a real <ul>, so swapping it in would
 * change what this panel renders, not just how it looks.
 */

const HEADING_BASE = 'revy-cjk mb-1 font-bold leading-[1.5]';
const BODY = 'revy-cjk text-[0.8rem] leading-[1.6]';
const LIST_ITEM = cn(BODY, 'pl-4');

/** Handle bold (**text**) and code (`text`) inline formatting */
function formatInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
      parts.push(<strong key={key++}>{boldMatch[2]}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    // Code: `text`
    const codeMatch = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
      parts.push(
        <code key={key++} className="rounded-[3px] bg-muted px-1 py-px text-[0.75rem]">
          {codeMatch[2]}
        </code>,
      );
      remaining = codeMatch[3];
      continue;
    }

    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return <>{parts}</>;
}

/** Simple Markdown-to-JSX renderer for basic formatting */
export function renderAnalysisMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('### ')) {
      elements.push(
        <h6 key={i} className={cn('text-[0.85rem]', HEADING_BASE, 'mt-3')}>
          {trimmed.slice(4)}
        </h6>,
      );
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <h5 key={i} className={cn('text-[0.9rem]', HEADING_BASE, 'mt-4')}>
          {trimmed.slice(3)}
        </h5>,
      );
    } else if (trimmed.startsWith('# ')) {
      elements.push(
        <h4 key={i} className={cn('text-[0.95rem]', HEADING_BASE, 'mt-4')}>
          {trimmed.slice(2)}
        </h4>,
      );
    } else if (trimmed.startsWith('> ')) {
      elements.push(
        <div
          key={i}
          className="my-1 rounded-r border-l-[3px] border-warning bg-accent py-0.5 pl-3"
        >
          <span className="revy-cjk text-xs leading-[1.5]">{trimmed.slice(2)}</span>
        </div>,
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      elements.push(
        <p key={i} className={LIST_ITEM}>
          {'• '}
          {formatInline(trimmed.slice(2))}
        </p>,
      );
    } else if (/^\d+\.\s/.test(trimmed)) {
      const match = trimmed.match(/^(\d+)\.\s(.*)$/);
      if (match) {
        elements.push(
          <p key={i} className={LIST_ITEM}>
            {match[1]}. {formatInline(match[2])}
          </p>,
        );
      }
    } else if (trimmed === '') {
      elements.push(<div key={i} className="h-1" />);
    } else {
      elements.push(
        <p key={i} className={BODY}>
          {formatInline(trimmed)}
        </p>,
      );
    }
  });

  return <>{elements}</>;
}
